import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Info } from "lucide-react";
import { getRealizadoResumo } from "../../api";
import RealizadoLayout from "../../components/gestaoRealizado/RealizadoLayout";
import {
  AvisoErro, BarraFiltros, Card, Carregando, GradeKpis, Kpi, MESES, ANO_ATUAL, Seletor, botaoPequeno,
  estiloTabela, formatCompacto, formatData, formatValor, opcoesAno, opcoesEmpresa, useCarregar, useTema,
} from "../../components/gestaoRealizado/comum";

// Visão geral do realizado de faturamento no ano. Duas leituras que NÃO se somam:
// "Solicitado" (formulário de NF, por competência) e "Competências controladas" (aba
// Controle comp., com baixas por NF). Ver backend/services/realizado_faturamento_svc.py.

const LINHAS = [
  { grupo: "Solicitado (formulário de NF) por competência" },
  { chave: "solicitadoBacklog", label: "Backlog (todo o faturamento do formulário)", dre: "1.1", total: true },
  { grupo: "Competências controladas (Controle comp.)" },
  { chave: "competencias", label: "Valor das competências" },
  { chave: "baixado", label: "(−) Baixado (NF, cancelamento, repasse)" },
  { chave: "cancelado", label: "   dos quais cancelamento de vendas", dre: "1.3", sub: true },
  { chave: "saldo", label: "Saldo a faturar", total: true },
];

export default function VisaoGeralRealizado() {
  const [isLight, setIsLight, C] = useTema();
  const navigate = useNavigate();
  const [ano, setAno] = useState(ANO_ATUAL);
  const [empresa, setEmpresa] = useState("");
  const { dados, carregando, erro } = useCarregar(() => getRealizadoResumo(ano, empresa), [ano, empresa]);
  const T = estiloTabela(C);
  const k = dados?.kpis;

  return (
    <RealizadoLayout titulo="Visão geral" isLight={isLight} setIsLight={setIsLight}>
      <BarraFiltros>
        <Seletor C={C} rotulo="Ano" value={ano} onChange={(v) => setAno(Number(v))} opcoes={opcoesAno} />
        <Seletor C={C} rotulo="Empresa" value={empresa} onChange={setEmpresa} opcoes={opcoesEmpresa} />
      </BarraFiltros>
      <AvisoErro C={C}>{erro}</AvisoErro>

      {carregando && !dados ? <Carregando C={C} /> : dados && (
        <>
          <GradeKpis>
            <Kpi C={C} rotulo={`Solicitado ${ano}`} valor={formatCompacto(k.solicitadoAno)} detalhe="Σ competências pedidas no formulário" />
            <Kpi C={C} rotulo={`Competências ${ano}`} valor={formatCompacto(k.competenciasAno)} detalhe="Σ Controle de competência" />
            <Kpi C={C} rotulo="Saldo a faturar" valor={formatCompacto(k.saldoAberto)} detalhe={`${k.competenciasAbertas} competência(s) em aberto no ano`} cor={C.warning} />
            <Kpi C={C} rotulo="Pendências" valor={k.pendencias} detalhe="Erros não resolvidos no log" cor={k.pendencias ? C.danger : C.success} />
            <Kpi C={C} rotulo="Sincronização planilha" valor={k.syncPendentes} detalhe={k.syncPendentes ? "Solicitações pendentes/erro" : "Tudo sincronizado"} cor={k.syncPendentes ? C.danger : C.success} />
          </GradeKpis>

          <Card
            C={C}
            titulo={`Realizado de faturamento · ${ano}${empresa ? ` · ${empresa}` : ""}`}
            subtitulo={k.ultimaImportacao ? `Última importação de CSV em ${formatData(k.ultimaImportacao)}` : "Nenhuma importação de CSV ainda"}
            acoes={<button style={botaoPequeno(C.brand, true)} onClick={() => navigate("/gestao-realizado/importacao")}>Importar CSV</button>}
          >
            <div style={T.wrap}>
              <table style={{ ...T.table, minWidth: 1100 }}>
                <thead>
                  <tr>
                    <th style={T.th}>Linha</th>
                    <th style={T.th}>DRE</th>
                    {MESES.map((m) => <th key={m} style={{ ...T.th, ...T.thNum }}>{m}</th>)}
                    <th style={{ ...T.th, ...T.thNum }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {LINHAS.map((l) => {
                    if (l.grupo) {
                      return (
                        <tr key={l.grupo}>
                          <td colSpan={15} style={{ ...T.td, fontWeight: 800, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", color: C.brand, background: C.bg }}>{l.grupo}</td>
                        </tr>
                      );
                    }
                    const serie = dados.series[l.chave];
                    const total = serie.reduce((a, b) => a + b, 0);
                    const estilo = { ...T.td, ...(l.total ? T.total : {}), ...(l.alerta && total ? { color: C.warning } : {}), ...(l.sub ? { color: C.muted } : {}) };
                    return (
                      <tr key={l.chave}>
                        <td style={{ ...estilo, whiteSpace: "pre" }}>{l.label}</td>
                        <td style={{ ...estilo, color: C.muted }}>{l.dre || ""}</td>
                        {serie.map((v, i) => <td key={i} style={{ ...estilo, ...T.tdNum }}>{formatValor(v)}</td>)}
                        <td style={{ ...estilo, ...T.tdNum, fontWeight: 800 }}>{formatValor(total)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 12, fontSize: 12, color: C.muted, alignItems: "flex-start" }}>
              <Info size={14} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>
                <b>Solicitado</b> e <b>Competências controladas</b> são leituras diferentes e não se somam: o Controle comp. só registra
                competências acompanhadas até a emissão da NF (principalmente GT4W), e os nomes de projeto das duas abas não coincidem.
                Todo faturamento do formulário é Backlog (linha 1.1 da DRE); Nova Venda não passa pelo formulário.
                A regra de qual das duas leituras alimenta a DRE ainda será definida pelo financeiro.
              </span>
            </div>
          </Card>
        </>
      )}
    </RealizadoLayout>
  );
}
